import { Item, Section } from '@react-stately/collections'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { Select } from 'components/Select/Select'
import { ExternalIntegrationItem } from './ExternalIntegrationItem'
import { integrationToName } from 'components/IntegrationRequiredModal/IntegrationRequiredModal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { SelectProps } from 'components/Select/Types'
import { Button } from 'blaze-ui/Button'
import { ReactComponent } from 'react-hotkeys'
import { CollectionElement } from '@react-types/shared'

interface Props extends Omit<SelectProps<{}>, 'children'> {
  onChange: (externalIntegrationId: number) => void
  selectedExternalIntegrationId?: number | null | string
  externalIntegrations: ExternalIntegration[]
  primaryProvider?: ExternalIntegrationEnum
  renderSelectValue?: (item: any) => JSX.Element
  forceProvider?: ExternalIntegrationEnum
  className?: string
  isDisabled?: boolean
  showName?: boolean
  itemClassName?: string
  labelClassName?: string
  includeAllAccountsOption?: boolean
  ButtonComponent?: ReactComponent
  renderPlatformIcon?: boolean
}

export const ExternalIntegrationSelect = ({
  onChange,
  selectedExternalIntegrationId,
  externalIntegrations,
  primaryProvider,
  renderSelectValue,
  forceProvider,
  size = 'lg',
  variant = 'tertiary',
  className,
  isDisabled,
  showName,
  itemClassName,
  labelClassName,
  includeAllAccountsOption = false,
  ButtonComponent = Button,
  renderPlatformIcon = false,
  ...rest
}: Props) => {
  const { isMobile } = useResponsiveDesign()

  const defaultRenderSelectValue = (item: any) => {
    if (!item) return null

    // Handle "All Accounts" explicitly
    if (item.key === 'all') {
      return 'All Accounts'
    }

    const selectedExternalIntegration = externalIntegrations.find(
      (externalIntegration) => externalIntegration.id.toString() === item.key,
    )

    if (!selectedExternalIntegration) return null

    return (
      <ExternalIntegrationItem
        externalIntegration={selectedExternalIntegration}
        renderPlatformIcon={renderPlatformIcon}
        size={size}
        showName={showName || !isMobile}
        nameClassName={labelClassName}
      />
    )
  }

  const integrationsWithOptionalAll = includeAllAccountsOption
    ? [{ id: 'all', provider: 'All Accounts' }, ...externalIntegrations]
    : externalIntegrations

  if (forceProvider) {
    const filteredIntegrations = integrationsWithOptionalAll.filter((exInt) => exInt.provider === forceProvider)

    return (
      <Select
        size={size}
        selectedKey={selectedExternalIntegrationId?.toString() ?? undefined}
        onSelectionChange={(key) => onChange(parseInt(key as string))}
        aria-label="Select integration"
        renderSelectValue={renderSelectValue ?? defaultRenderSelectValue}
        variant={variant}
        spanClass={className}
        isDisabled={isDisabled}
        ButtonComponent={ButtonComponent}
        useFullWidth={true}
        {...rest}
      >
        {filteredIntegrations.map((externalIntegration) => (
          <Item key={externalIntegration.id} textValue={externalIntegration.provider}>
            {externalIntegration.id === 'all' ? (
              'All Accounts'
            ) : (
              <ExternalIntegrationItem
                externalIntegration={externalIntegration as ExternalIntegration}
                renderPlatformIcon
                size={size}
                isSelected={selectedExternalIntegrationId === externalIntegration.id}
                nameClassName={itemClassName}
              />
            )}
          </Item>
        ))}
      </Select>
    )
  }

  if (
    primaryProvider &&
    externalIntegrations.length > 0 &&
    externalIntegrations.some((externalIntegration) => externalIntegration.provider === primaryProvider)
  ) {
    const primaryIntegrations = []
    const otherIntegrations = []

    for (const externalIntegration of integrationsWithOptionalAll) {
      if (externalIntegration.provider === primaryProvider) {
        primaryIntegrations.push(externalIntegration)
      } else {
        otherIntegrations.push(externalIntegration)
      }
    }

    return (
      <Select
        size={size}
        selectedKey={selectedExternalIntegrationId?.toString() ?? undefined}
        onSelectionChange={(key) => onChange(parseInt(key as string))}
        aria-label="Select integration"
        renderSelectValue={renderSelectValue ?? defaultRenderSelectValue}
        variant={variant}
        ButtonComponent={ButtonComponent}
        useFullWidth={true}
        {...rest}
      >
        {
          [
            <Section title={integrationToName[primaryProvider]} key="Primary">
              {primaryIntegrations.map((externalIntegration) => (
                <Item key={externalIntegration.id} textValue={externalIntegration.provider}>
                  {externalIntegration.id === 'all' ? (
                    'All Accounts'
                  ) : (
                    <ExternalIntegrationItem
                      externalIntegration={externalIntegration as ExternalIntegration}
                      nameClassName={labelClassName}
                      renderPlatformIcon={renderPlatformIcon}
                    />
                  )}
                </Item>
              ))}
            </Section>,
            otherIntegrations.length > 0 && (
              <Section
                title={
                  otherIntegrations.length === 1
                    ? integrationToName[otherIntegrations[0].provider as ExternalIntegrationEnum]
                    : 'Other accounts'
                }
                key="Other"
              >
                {otherIntegrations.map((externalIntegration) => (
                  <Item key={externalIntegration.id} textValue={externalIntegration.provider}>
                    {externalIntegration.id === 'all' ? (
                      'All Accounts'
                    ) : (
                      <ExternalIntegrationItem
                        externalIntegration={externalIntegration as ExternalIntegration}
                        renderPlatformIcon
                        nameClassName={labelClassName}
                        size={size}
                      />
                    )}
                  </Item>
                ))}
              </Section>
            ),
          ].filter(Boolean) as CollectionElement<object>[]
        }
      </Select>
    )
  }

  return (
    <Select
      size={size}
      selectedKey={selectedExternalIntegrationId?.toString() ?? undefined}
      onSelectionChange={(key) => onChange(parseInt(key as string))}
      aria-label="Select integration"
      renderSelectValue={renderSelectValue ?? defaultRenderSelectValue}
      variant={variant}
      ButtonComponent={ButtonComponent}
      useFullWidth={true}
      {...rest}
    >
      {integrationsWithOptionalAll.map((externalIntegration) => (
        <Item key={externalIntegration.id} textValue={externalIntegration.provider}>
          {externalIntegration.id === 'all' ? (
            'All Accounts'
          ) : (
            <ExternalIntegrationItem
              externalIntegration={externalIntegration as ExternalIntegration}
              renderPlatformIcon
              size={size}
              nameClassName={labelClassName}
            />
          )}
        </Item>
      ))}
    </Select>
  )
}
